import React from 'react';
import { useNavigate } from 'react-router-dom';
import { getTierName, getFeatureName } from '../config/features';

/**
 * UpgradePrompt - Shows a prompt to upgrade when a feature is locked
 * @param {string} feature - The feature key that requires upgrade
 * @param {string} requiredTier - The tier required to access the feature
 * @param {string} variant - 'card' (default), 'inline', or 'banner'
 */
function UpgradePrompt({ feature, requiredTier, variant = 'card' }) {
  const navigate = useNavigate();
  const tierName = getTierName(requiredTier);
  const featureName = getFeatureName(feature);

  const handleLearnMore = () => {
    // /settings has no billing section; plans and upgrade paths live on /pricing.
    navigate('/pricing');
  };

  if (variant === 'inline') {
    return (
      <span className="upgrade-prompt-inline">
        <span className="lock-icon">&#128274;</span>
        <span className="upgrade-text">
          Upgrade to {tierName} to unlock
        </span>
      </span>
    );
  }

  if (variant === 'banner') {
    return (
      <div className="upgrade-prompt-banner">
        <span className="lock-icon">&#128274;</span>
        <span className="upgrade-text">
          <strong>{featureName}</strong> requires {tierName} plan
        </span>
        <button className="upgrade-btn-small" onClick={handleLearnMore}>
          Upgrade
        </button>
        <style>{styles}</style>
      </div>
    );
  }

  // Default: card variant
  return (
    <div className="upgrade-prompt-card">
      <div className="upgrade-icon">&#128274;</div>
      <h3 className="upgrade-title">Upgrade to {tierName}</h3>
      <p className="upgrade-description">
        Unlock <strong>{featureName}</strong> and more powerful features for your business.
      </p>
      <button className="upgrade-btn" onClick={handleLearnMore}>
        Learn More
      </button>
      <style>{styles}</style>
    </div>
  );
}

const styles = `
  .upgrade-prompt-card {
    background: linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%);
    border: 2px dashed #cbd5e1;
    border-radius: 12px;
    padding: 32px 24px;
    text-align: center;
    max-width: 400px;
    margin: 20px auto;
  }

  .upgrade-icon {
    font-size: 3rem;
    margin-bottom: 16px;
    opacity: 0.7;
  }

  .upgrade-title {
    font-size: 1.25rem;
    font-weight: 600;
    color: #1e293b;
    margin: 0 0 12px 0;
  }

  .upgrade-description {
    font-size: 0.95rem;
    color: #64748b;
    margin: 0 0 20px 0;
    line-height: 1.5;
  }

  .upgrade-btn {
    background: linear-gradient(135deg, #7c3aed 0%, #6d28d9 100%);
    color: white;
    border: none;
    padding: 12px 28px;
    border-radius: 8px;
    font-size: 1rem;
    font-weight: 600;
    cursor: pointer;
    transition: all 0.2s ease;
  }

  .upgrade-btn:hover {
    background: linear-gradient(135deg, #6d28d9 0%, #5b21b6 100%);
    transform: translateY(-1px);
    box-shadow: 0 4px 12px rgba(109, 40, 217, 0.3);
  }

  .upgrade-prompt-banner {
    display: flex;
    align-items: center;
    gap: 12px;
    background: linear-gradient(135deg, #fef3c7 0%, #fde68a 100%);
    border: 1px solid #f59e0b;
    border-radius: 8px;
    padding: 12px 16px;
    margin: 12px 0;
  }

  .upgrade-prompt-banner .lock-icon {
    font-size: 1.25rem;
  }

  .upgrade-prompt-banner .upgrade-text {
    flex: 1;
    font-size: 0.9rem;
    color: #92400e;
  }

  .upgrade-btn-small {
    background: #f59e0b;
    color: white;
    border: none;
    padding: 6px 16px;
    border-radius: 6px;
    font-size: 0.85rem;
    font-weight: 600;
    cursor: pointer;
    transition: background 0.2s ease;
  }

  .upgrade-btn-small:hover {
    background: #d97706;
  }

  .upgrade-prompt-inline {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    color: #9ca3af;
    font-size: 0.85rem;
  }

  .upgrade-prompt-inline .lock-icon {
    font-size: 0.9rem;
  }
`;

export default UpgradePrompt;
